import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpEvent, HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { CONFIG } from '../../config';
import { ExceptionService } from '../../xform-compat';
import {
  MediaScope,
  MediaUploadResult,
  ReleaseNote,
  ReleaseNotePermission,
  ReleaseNotePermissionPreset,
  ReleaseNoteRevision,
  SimpleReleaseNote,
  SplashCard
} from '../../shared/models/release-note/release-note.model';

/**
 * Thrown by {@link ReleaseNoteService#save} and {@link ReleaseNoteService#discard} instead of a translated
 * message when another author changed the revision first (409 `RELEASE_NOTE_REVISION_STALE`), so the editor
 * can offer a reload.
 */
export const RELEASE_NOTE_STALE = 'RELEASE_NOTE_STALE';

const STALE_CODE = 'BUS_1243';
const BASE = '/api/rms/release-notes';

/**
 * Data service for the release "What's New" feature (RA-2695) over the RA-2764 API. Not `AbstractCRUD`:
 * the API is not CRUD-shaped. Every JSON call pipes `catchBadResponse`, which rethrows the translated
 * `API_ERRORS.<code>` string for the caller to banner — except {@link getSplash}, which must never banner.
 */
@Injectable({
  providedIn: 'root'
})
export class ReleaseNoteService {

  constructor(
    private readonly http: HttpClient,
    private readonly exceptionService: ExceptionService
  ) {}

  /**
   * The splash card for the current user, or null when there is nothing to show (204). 🔴 Errors are
   * passed through raw, never through `catchBadResponse`: the splash host swallows them silently.
   */
  public getSplash(): Observable<SplashCard | null> {
    return this.http.get<SplashCard>(`${BASE}/splash`, { observe: 'response' })
      .pipe(map((response: HttpResponse<SplashCard>) => response.status === 204 ? null : response.body));
  }

  /** Every release visible to the caller, newest first. Always one large page — the API defaults to 25. */
  public getLibrary(): Observable<Array<SimpleReleaseNote>> {
    return this.getRecords(BASE);
  }

  /** The current published revision of a visible release, pages already permission-filtered. */
  public getRelease(id: number): Observable<ReleaseNote> {
    return this.json(this.http.get<ReleaseNote>(`${BASE}/${id}`));
  }

  /** Records that the caller has seen a release. Idempotent; a no-op server-side unless it is visible. */
  public markSeen(id: number): Observable<void> {
    return this.json(this.http.post<void>(`${BASE}/${id}/views`, {}));
  }

  /** The media bytes for a stored ref, with the bearer header — a bare `<img src>` would 401. */
  public getMediaBlob(ref: string, scope: MediaScope): Observable<Blob> {
    const path = scope === 'authoring' ? `${BASE}/authoring/media` : `${BASE}/media`;
    return this.http.get(path, { params: { ref }, responseType: 'blob' });
  }

  /** The authoring catalogue: every release, each described by its draft when it has one. */
  public getCatalogue(): Observable<Array<SimpleReleaseNote>> {
    return this.getRecords(`${BASE}/authoring`);
  }

  /** The authoring view: the draft when there is one, else the current revision, pages unfiltered. */
  public getForEditing(id: number): Observable<ReleaseNote> {
    return this.json(this.http.get<ReleaseNote>(`${BASE}/authoring/${id}`));
  }

  public getRevisions(id: number): Observable<Array<ReleaseNoteRevision>> {
    return this.json(this.http.get<Array<ReleaseNoteRevision>>(`${BASE}/authoring/${id}/revisions`));
  }

  /** Creates a release with its first draft. */
  public create(body: ReleaseNote): Observable<ReleaseNote> {
    return this.json(this.http.post<ReleaseNote>(`${BASE}/authoring`, body));
  }

  /**
   * Saves a revision in place. The body must carry `revisionUpdated` from the last read; a stale one fails
   * with {@link RELEASE_NOTE_STALE} rather than a translated message.
   */
  public save(id: number, revisionId: number, body: ReleaseNote): Observable<ReleaseNote> {
    return this.tokened(this.http.put<ReleaseNote>(`${BASE}/authoring/${id}/revisions/${revisionId}`, body));
  }

  /** Clones into a new draft: the current revision when no source is given, else rolls back to it. */
  public clone(id: number, sourceRevisionId?: number): Observable<ReleaseNote> {
    const body = sourceRevisionId ? { sourceRevisionId } : {};
    return this.json(this.http.post<ReleaseNote>(`${BASE}/authoring/${id}/revisions`, body));
  }

  /**
   * Throws away a live release's unpublished draft; returns the release as now published. Like {@link save},
   * it carries the `revisionUpdated` the draft was loaded with — sent back verbatim as a query parameter — and a
   * stale one fails with {@link RELEASE_NOTE_STALE}.
   */
  public discard(id: number, revisionId: number, revisionUpdated: string): Observable<ReleaseNote> {
    return this.tokened(this.http.delete<ReleaseNote>(`${BASE}/authoring/${id}/revisions/${revisionId}`,
      { params: { revisionUpdated } }));
  }

  /** Publishes now, schedules (`visibleFrom`, UTC server format), or reschedules a pending release. */
  public publish(id: number, visibleFrom?: string): Observable<ReleaseNote> {
    const body = visibleFrom ? { visibleFrom } : {};
    return this.json(this.http.post<ReleaseNote>(`${BASE}/authoring/${id}/publish`, body));
  }

  public withdraw(id: number): Observable<ReleaseNote> {
    return this.json(this.http.post<ReleaseNote>(`${BASE}/authoring/${id}/withdraw`, {}));
  }

  public restore(id: number): Observable<ReleaseNote> {
    return this.json(this.http.post<ReleaseNote>(`${BASE}/authoring/${id}/restore`, {}));
  }

  /** Every active permission, `shortName` carrying its group. */
  public getPermissions(): Observable<Array<ReleaseNotePermission>> {
    return this.json(this.http.get<Array<ReleaseNotePermission>>(`${BASE}/authoring/permissions`));
  }

  public getPresets(): Observable<Array<ReleaseNotePermissionPreset>> {
    return this.json(this.http.get<Array<ReleaseNotePermissionPreset>>(`${BASE}/authoring/permission-presets`));
  }

  /**
   * Uploads one file with its own progress events, so several slots can upload at once without sharing
   * state (unlike `FileUploadService`).
   */
  public uploadMedia(file: File): Observable<HttpEvent<MediaUploadResult>> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<MediaUploadResult>(`${BASE}/media`, form, { reportProgress: true, observe: 'events' })
      .pipe(catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }

  /** A write that carries the concurrency token: a stale one (keyed on the error code, not the status) is the sentinel. */
  private tokened<T>(request: Observable<T>): Observable<T> {
    return request.pipe(catchError(ex => ex instanceof HttpErrorResponse && ex.error?.code === STALE_CODE
      ? throwError(RELEASE_NOTE_STALE)
      : this.exceptionService.catchBadResponse(ex)));
  }

  private getRecords(path: string): Observable<Array<SimpleReleaseNote>> {
    const params = { page: '0', pagesize: `${CONFIG.pagination.singleLargePage}` };
    return this.json(this.http.get<{ records?: Array<SimpleReleaseNote> }>(path, { params }))
      .pipe(map(page => page?.records ?? []));
  }

  private json<T>(request: Observable<T>): Observable<T> {
    return request.pipe(catchError(ex => this.exceptionService.catchBadResponse(ex)));
  }
}
